/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '../../hooks/common/use-application-state'
import { loadPinnedNotes } from '../../redux/pinned-notes/methods'
import { Welcome } from '../explore-page/welcome'
import { PinnedNotes } from '../explore-page/pinned-notes/pinned-notes'
import { useUiNotifications } from '../notifications/ui-notification-boundary'
import { buildNoteTree, collectNotesInFolder, TOP_NOTE_PATH } from '../note-tree/build-note-tree'
import { FolderCard } from './folder-card'
import { RecentNotesList } from './recent-notes-list'
import { sortByLastChangedDesc } from './sort-by-last-changed'
import React, { Fragment, useEffect, useMemo } from 'react'
import { Card, Col, Container, Row } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'

const RECENT_NOTES_COUNT = 10

/**
 * Renders the dashboard on the home page: pinned notes, the most recently changed notes and a card per top-level folder.
 */
export const HomePageContent: React.FC = () => {
  const { t } = useTranslation()
  const { showErrorNotificationBuilder } = useUiNotifications()
  const entries = useApplicationState((state) => state.noteTree.entries)
  const loaded = useApplicationState((state) => state.noteTree.loaded)

  useEffect(() => {
    loadPinnedNotes().catch(showErrorNotificationBuilder('explore.pinnedNotes.loadingError'))
  }, [showErrorNotificationBuilder])

  const allNotes = useMemo(() => Object.values(entries), [entries])
  const tree = useMemo(() => buildNoteTree(allNotes), [allNotes])
  const recentNotes = useMemo(() => sortByLastChangedDesc(allNotes).slice(0, RECENT_NOTES_COUNT), [allNotes])

  return (
    <Fragment>
      <Container>
        <Welcome />
      </Container>
      <PinnedNotes />
      <Container className={'mb-5'}>
        {loaded && allNotes.length === 0 && (
          <p className={'text-body-secondary'}>
            <Trans i18nKey={'noteTree.empty'} />
          </p>
        )}
        {recentNotes.length > 0 && (
          <Card className={'mb-4'}>
            <Card.Header>
              <strong>
                <Trans i18nKey={'homePage.recentlyChanged'} />
              </strong>
            </Card.Header>
            <RecentNotesList notes={recentNotes} showPath={true} />
          </Card>
        )}
        <Row xs={1} lg={2} className={'g-4'}>
          {tree.top.length > 0 && (
            <Col>
              <FolderCard name={t('noteTree.top')} path={TOP_NOTE_PATH} notes={tree.top} />
            </Col>
          )}
          {tree.folders.map((folder) => (
            <Col key={folder.path}>
              <FolderCard name={folder.name} path={folder.path} notes={collectNotesInFolder(folder)} />
            </Col>
          ))}
          {tree.unfiled.length > 0 && (
            <Col>
              <FolderCard name={t('noteTree.unfiled')} notes={tree.unfiled} />
            </Col>
          )}
        </Row>
      </Container>
    </Fragment>
  )
}
